import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { GENERATE_AUTH_TOKEN } from '../config';
import { Undo2, Redo2, Save, Loader2, Check, ArrowUp, Plus, Pencil, MousePointer2, Eraser, RefreshCw, Hand, X, ImageIcon, Camera, ChevronDown, ChevronLeft, Copy } from 'lucide-react';
import AngleSelector3D from '../components/AngleSelector3D';
import SubscriptionLimitModal from '../components/SubscriptionLimitModal';
import { useSidebar } from '../context/SidebarContext';

export const EditarImagen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sidebarWidth, setMobileBackOverride } = useSidebar();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const imageIdParam = (params.get('id') || '').trim();
  const companyId = (params.get('companyId') || '').trim();
  // imagenes_generadas.id es UUID (string)
  const imageId: string = imageIdParam;

  const [loading, setLoading] = useState<boolean>(true);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [instruction, setInstruction] = useState<string>('');
  const [history, setHistory] = useState<{ id: string; url: string }[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(-1);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [showEditSuccess, setShowEditSuccess] = useState<boolean>(false);
  const [isWaitingForImageLoad, setIsWaitingForImageLoad] = useState<boolean>(false);
  const [showSubscriptionLimitModal, setShowSubscriptionLimitModal] = useState<boolean>(false);
  const [showEditModeMenu, setShowEditModeMenu] = useState<boolean>(false);
  const [showSaveMenu, setShowSaveMenu] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<'instruction' | 'precise' | 'eraser' | 'product_change' | 'angle_change'>('instruction');
  const [subscriptionLimitType, setSubscriptionLimitType] = useState<'free-trial-ended' | 'plan-limit-reached'>('free-trial-ended');
  const [showModerationError, setShowModerationError] = useState<boolean>(false);
  const [showHelpPopup, setShowHelpPopup] = useState<boolean>(false);
  const [userRubro, setUserRubro] = useState<string>('Moda e Indumentaria');
  const desktopInputRef = useRef<HTMLTextAreaElement>(null);
  
  // Precise edit mode state
  const [hasMask, setHasMask] = useState<boolean>(false);
  const maskCanvasRef = useRef<HTMLCanvasElement>(null);
  const maskCanvasMobileRef = useRef<HTMLCanvasElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const imageMobileRef = useRef<HTMLImageElement>(null);
  const [isDrawing, setIsDrawing] = useState<boolean>(false);
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null);
  
  // Product change mode state
  interface ProductVariant {
    id: string;
    nombre: string;
    imageUrl: string | null;
  }
  const [showProductPicker, setShowProductPicker] = useState<boolean>(false);
  const [products, setProducts] = useState<ProductVariant[]>([]);
  const [loadingProducts, setLoadingProducts] = useState<boolean>(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductVariant | null>(null);
  
  // Angle change mode state
  const [angleSelection, setAngleSelection] = useState<{ horizontal_angle: number; vertical_angle: number; zoom: number } | null>(null);

  // Enable Save only when the current image differs from the original (i.e., after at least one edit and not undone)
  const hasEdits = currentIndex > 0;

  // Prevent body scroll on mobile
  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, []);

  const checkSubscriptionLimits = async (): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!profile?.id_company) throw new Error('No company associated with user');

      const { data: companyProfile } = await supabase
        .from('company_profiles')
        .select('plan, imagenes_restantes')
        .eq('id_company', profile.id_company)
        .single();

      if (!companyProfile) throw new Error('No company profile found');

      const remaining = (companyProfile as any).imagenes_restantes as number | null | undefined;
      if (typeof remaining === 'number' && remaining <= 0) {
        const isFree = (companyProfile as any).plan === 'gratis';
        setSubscriptionLimitType(isFree ? 'free-trial-ended' : 'plan-limit-reached');
        setShowSubscriptionLimitModal(true);
        return false;
      }

      return true;
    } catch (err) {
      console.error('Error checking subscription limits:', err);
      return true;
    }
  };

  // Fetch user's rubro on mount
  useEffect(() => {
    const fetchRubro = async () => {
      if (!companyId) return;
      try {
        const { data: company } = await supabase
          .from('company_profiles')
          .select('rubro')
          .eq('id_company', companyId)
          .single();

        if (company?.rubro) {
          setUserRubro(company.rubro as string);
        }
      } catch (err) {
        // Keep default rubro on error
      }
    };
    fetchRubro();
  }, [companyId]);

  useEffect(() => {
    const fetchImage = async () => {
      if (!imageIdParam || !companyId) {
        setLoading(false);
        setError('No se encontró información de la imagen.');
        return;
      }
      try {
        setLoading(true);
        setError(null);
        // Buscar por id (UUID string); fallback a file_name exacto o prefijo
        let imageRow: { id: string; file_name: string } | null = null;
        const { data: byId, error: byIdErr } = await supabase
          .from('imagenes_generadas')
          .select('id, file_name')
          .eq('id', imageIdParam)
          .eq('id_company', companyId)
          .maybeSingle();
        if (byId && !byIdErr) imageRow = byId as any;
        if (!imageRow) {
          const { data: exactData } = await supabase
            .from('imagenes_generadas')
            .select('id, file_name')
            .eq('file_name', imageIdParam)
            .eq('id_company', companyId)
            .maybeSingle();
          if (exactData) imageRow = exactData as any;
        }
        if (!imageRow) {
          const { data: likeData } = await supabase
            .from('imagenes_generadas')
            .select('id, file_name')
            .like('file_name', `${imageIdParam}.%`)
            .eq('id_company', companyId)
            .maybeSingle();
          if (likeData) imageRow = likeData as any;
        }
        if (!imageRow) throw new Error('Imagen no disponible');
        if (!imageRow?.file_name) throw new Error('Imagen no disponible');

        const filePath = `${companyId}/full/${imageRow.file_name}`;
        const { data: signed } = await supabase.storage
          .from('generadas')
          .createSignedUrl(filePath, 3600);
        if (signed?.signedUrl) {
          setImageUrl(signed.signedUrl);
          setHistory([{ id: imageRow.id as string, url: signed.signedUrl }]);
          setCurrentIndex(0);
        } else {
          throw new Error('No se pudo obtener la imagen');
        }
      } catch (e: any) {
        console.error('Error cargando imagen para edición:', e);
        setError('Error al cargar la imagen para edición.');
      } finally {
        setLoading(false);
      }
    };

    fetchImage();
  }, [imageId, companyId]);

  // Keep imageUrl in sync with the current history index
  useEffect(() => {
    if (currentIndex >= 0 && history[currentIndex]) {
      setImageUrl(history[currentIndex].url);
    }
  }, [currentIndex, history]);

  // Auto-hide the success chip a few seconds after a successful edit
  useEffect(() => {
    if (!showEditSuccess) return;
    const t = window.setTimeout(() => setShowEditSuccess(false), 3000);
    return () => window.clearTimeout(t);
  }, [showEditSuccess]);

  // Helper: auto-resize a textarea up to 3 lines and then make it scrollable
  const autoResizeTextarea = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    const baseHeight = 44; // px: altura base equivalente al input original (h-11)
    const text = el.value || '';

    // Sin texto: altura base fija, sin scroll
    if (!text) {
      el.style.height = `${baseHeight}px`;
      el.style.overflowY = 'hidden';
      return;
    }

    // Disable transition during measurement so 'auto' doesn't break animation
    el.style.transition = 'none';
    el.style.height = 'auto';
    const scroll = el.scrollHeight;

    // Umbrales discretos: 1 línea, 2 líneas o 3 líneas máximo
    const oneLineThreshold = baseHeight + 4; // pequeña tolerancia
    const twoLineThreshold = baseHeight * 2 + 4;

    let lines = 1;
    if (scroll > twoLineThreshold) {
      lines = 3;
    } else if (scroll > oneLineThreshold) {
      lines = 2;
    }

    const targetHeight = baseHeight * lines;
    // Force layout read, then re-enable transition for smooth animation
    el.getBoundingClientRect();
    el.style.transition = 'height 200ms ease';
    el.style.height = `${targetHeight}px`;
    el.style.overflowY = lines === 3 && scroll > targetHeight ? 'auto' : 'hidden';
  };

  // Fetch products for product change mode
  const fetchProducts = async () => {
    if (!companyId) return;
    setLoadingProducts(true);
    try {
      // Fetch variants
      const { data: variantsData, error: variantsError } = await supabase
        .from('variantes')
        .select('id, nombre')
        .eq('activo', true)
        .eq('id_company', companyId)
        .order('created_at', { ascending: false });

      if (variantsError) throw variantsError;

      if (!variantsData || variantsData.length === 0) {
        setProducts([]);
        return;
      }

      const variantIds = variantsData.map(v => v.id);

      // Fetch principal images for each variant
      const { data: imagesData, error: imagesError } = await supabase
        .from('productos_imagenes')
        .select('url, variante_id')
        .eq('id_company', companyId)
        .eq('principal', true)
        .in('variante_id', variantIds);

      if (imagesError) throw imagesError;

      // Get signed URLs for each image
      const imagesByVariant: Record<string, string> = {};
      for (const img of (imagesData || [])) {
        const urlKey = img.url as string;
        const variantId = img.variante_id as string;
        
        if (urlKey.startsWith('http')) {
          // Already a full URL
          imagesByVariant[variantId] = urlKey;
        } else {
          // Need to get signed URL - try optimized first, then full
          const optPath = `${companyId}/${variantId}/opt/${urlKey}`;
          const fullPath = `${companyId}/${variantId}/full/${urlKey}`;
          
          // Try optimized thumbnail first
          const { data: optData } = await supabase.storage
            .from('productos')
            .createSignedUrl(optPath, 3600);
          
          if (optData?.signedUrl) {
            imagesByVariant[variantId] = optData.signedUrl;
          } else {
            // Fallback to full image
            const { data: fullData } = await supabase.storage
              .from('productos')
              .createSignedUrl(fullPath, 3600);
            
            if (fullData?.signedUrl) {
              imagesByVariant[variantId] = fullData.signedUrl;
            }
          }
        }
      }

      // Build products array
      const productsList: ProductVariant[] = variantsData.map(v => ({
        id: v.id,
        nombre: v.nombre,
        imageUrl: imagesByVariant[v.id] || null
      }));

      setProducts(productsList);
    } catch (err) {
      console.error('Error fetching products:', err);
    } finally {
      setLoadingProducts(false);
    }
  };

  // Load products when entering product_change mode
  useEffect(() => {
    if (editMode === 'product_change' && products.length === 0) {
      fetchProducts();
    }
  }, [editMode]);

  // Clear mask when switching away from precise mode
  useEffect(() => {
    if (editMode !== 'precise') {
      setHasMask(false);
      // Clear both canvases
      const canvasDesktop = maskCanvasRef.current;
      const canvasMobile = maskCanvasMobileRef.current;
      if (canvasDesktop) {
        const ctx = canvasDesktop.getContext('2d');
        if (ctx) ctx.clearRect(0, 0, canvasDesktop.width, canvasDesktop.height);
      }
      if (canvasMobile) {
        const ctx = canvasMobile.getContext('2d');
        if (ctx) ctx.clearRect(0, 0, canvasMobile.width, canvasMobile.height);
      }
    }
  }, [editMode]);

  // Initialize canvas when image loads
  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>, isMobile: boolean = false) => {
    const img = e.currentTarget;
    setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
    
    // Resize canvas to match displayed image size using getBoundingClientRect for accuracy
    const canvas = isMobile ? maskCanvasMobileRef.current : maskCanvasRef.current;
    if (canvas) {
      const rect = img.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
    }
    
    // If we were waiting for the edited image to load, show success now
    if (isWaitingForImageLoad) {
      setIsWaitingForImageLoad(false);
      setIsSubmitting(false);
      setShowEditSuccess(true);
      // Clear instruction and mask now that the image has loaded
      setInstruction('');
      clearMask();
      // Clear selected product after product_change edit completes
      if (editMode === 'product_change') {
        setSelectedProduct(null);
      }
    }
  };

  // Re-initialize canvas when editMode changes to precise
  useEffect(() => {
    if (editMode === 'precise') {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        const isMobile = window.innerWidth < 768;
        const img = isMobile ? imageMobileRef.current : imageRef.current;
        const canvas = isMobile ? maskCanvasMobileRef.current : maskCanvasRef.current;
        if (img && canvas) {
          // Get the actual rendered dimensions
          const rect = img.getBoundingClientRect();
          canvas.width = rect.width;
          canvas.height = rect.height;
        }
      });
    }
  }, [editMode]);

  // Get mask as base64 PNG (RGBA with transparency for OpenAI)
  const getMaskBase64 = (): string | null => {
    const isMobile = window.innerWidth < 768;
    const canvas = isMobile ? maskCanvasMobileRef.current : maskCanvasRef.current;
    if (!canvas || !imageDimensions) return null;
    
    // Create a new canvas at the original image dimensions
    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = imageDimensions.width;
    outputCanvas.height = imageDimensions.height;
    const outputCtx = outputCanvas.getContext('2d');
    if (!outputCtx) return null;
    
    // Fill with black (areas to keep)
    outputCtx.fillStyle = 'black';
    outputCtx.fillRect(0, 0, outputCanvas.width, outputCanvas.height);
    
    // Scale and draw the mask (white areas = areas to edit)
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    
    // Get the display canvas data and scale it to original image size
    const scaleX = imageDimensions.width / canvas.width;
    const scaleY = imageDimensions.height / canvas.height;
    
    const sourceData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    
    // Draw white where the user painted (red areas become white in mask)
    for (let y = 0; y < canvas.height; y++) {
      for (let x = 0; x < canvas.width; x++) {
        const i = (y * canvas.width + x) * 4;
        // Check if pixel has red color (user painted here)
        if (sourceData.data[i] > 100 && sourceData.data[i + 3] > 50) {
          // Draw a scaled circle on the output
          const outX = Math.floor(x * scaleX);
          const outY = Math.floor(y * scaleY);
          const radius = Math.max(1, Math.floor(20 * scaleX));
          outputCtx.fillStyle = 'white';
          outputCtx.beginPath();
          outputCtx.arc(outX, outY, radius, 0, Math.PI * 2);
          outputCtx.fill();
        }
      }
    }
    
    return outputCanvas.toDataURL('image/png').split(',')[1];
  };

  // Drawing handlers for mask canvas - use event target to get the correct canvas
  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (editMode !== 'precise') return;
    setIsDrawing(true);
    draw(e);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    // Check if any mask was drawn on either canvas
    const isMobile = window.innerWidth < 768;
    const canvas = isMobile ? maskCanvasMobileRef.current : maskCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const hasContent = imageData.data.some((val, i) => i % 4 === 3 && val > 0);
        setHasMask(hasContent);
      }
    }
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing || editMode !== 'precise') return;
    // Get canvas from event target
    const canvas = e.currentTarget;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    let clientX: number, clientY: number;
    
    if ('touches' in e) {
      e.preventDefault(); // Prevent scrolling on mobile
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    // Scale coordinates to canvas internal size (fixes distortion)
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;

    // Draw solid red (no alpha) - we'll handle transparency via CSS
    ctx.fillStyle = 'rgb(239, 68, 68)';
    ctx.beginPath();
    ctx.arc(x, y, 20, 0, Math.PI * 2);
    ctx.fill();
  };

  const clearMask = () => {
    // Clear both canvases
    const canvasDesktop = maskCanvasRef.current;
    const canvasMobile = maskCanvasMobileRef.current;
    if (canvasDesktop) {
      const ctx = canvasDesktop.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, canvasDesktop.width, canvasDesktop.height);
    }
    if (canvasMobile) {
      const ctx = canvasMobile.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, canvasMobile.width, canvasMobile.height);
    }
    setHasMask(false);
  };

  const handleSendInstruction = async () => {
    try {
      // For product_change mode, we need a selected product instead of instruction
      if (editMode === 'product_change') {
        if (!selectedProduct) return;
      } else if (editMode === 'angle_change') {
        if (!angleSelection) return;
      } else {
        const trimmed = instruction.trim();
        if (!trimmed) return;
      }
      
      if (!companyId) {
        setError('Falta el companyId para editar.');
        return;
      }

      // Check subscription limits before editing
      const canGenerate = await checkSubscriptionLimits();
      if (!canGenerate) return;

      setIsSubmitting(true);
      setError(null);
      setShowEditSuccess(false);

      const { data: auth } = await supabase.auth.getUser();
      const user = auth?.user;
      if (!user) {
        setError('No se pudo identificar al usuario.');
        setIsSubmitting(false);
        return;
      }

      const currentImageId = (currentIndex >= 0 && history[currentIndex]
        ? history[currentIndex].id
        : imageId);

      // Build request body based on edit mode
      let requestBody: Record<string, unknown>;
      
      if (editMode === 'product_change' && selectedProduct) {
        requestBody = {
          user_id: user.id,
          imagen_id: currentImageId,
          variante_id: selectedProduct.id,
          mode: 'cambio_producto'
        };
      } else if (editMode === 'angle_change' && angleSelection) {
        requestBody = {
          user_id: user.id,
          imagen_id: currentImageId,
          mode: 'cambiar_angulo',
          horizontal_angle: angleSelection.horizontal_angle,
          vertical_angle: angleSelection.vertical_angle,
          zoom: angleSelection.zoom,
        };
      } else {
        requestBody = {
          user_id: user.id,
          imagen_id: currentImageId,
          instruction: instruction.trim(),
          mode: editMode === 'precise' ? 'edit_preciso' : 'edit_general'
        };
        
        // Add mask for precise edit mode
        if (editMode === 'precise' && hasMask) {
          const maskB64 = getMaskBase64();
          if (maskB64) {
            requestBody.mask = maskB64;
          }
        }
      }

      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/editor_ia', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': GENERATE_AUTH_TOKEN },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error('Error al enviar la instrucción');
      }

      const data = await response.json();

      // Check for moderation error (only applies to precise edit mode)
      if (data?.response === 'error_moderation') {
        setShowModerationError(true);
        setIsSubmitting(false);
        return;
      }

      // edited_image_id puede ser UUID (id) o file_name
      const newImageIdRaw: string | number | undefined = data?.edited_image_id ?? data?.imagen_id ?? data?.id;
      if (!newImageIdRaw) {
        throw new Error('No se recibió el ID de la imagen editada');
      }

      // Resolver la fila en DB (siempre como strings UUID/file_name)
      let newRow: { id: string; file_name: string } | null = null;
      const newIdStr = String(newImageIdRaw);
      // Intentar por id (UUID)
      const { data: idMatch } = await supabase
        .from('imagenes_generadas')
        .select('id, file_name')
        .eq('id', newIdStr)
        .eq('id_company', companyId)
        .maybeSingle();
      if (idMatch) newRow = idMatch as any;
      // Intentar por file_name
      if (!newRow) {
        const { data: exact } = await supabase
          .from('imagenes_generadas')
          .select('id, file_name')
          .eq('file_name', newIdStr)
          .eq('id_company', companyId)
          .maybeSingle();
        if (exact) newRow = exact as any;
      }
      // Intentar prefijo file_name
      if (!newRow) {
        const { data: likeData } = await supabase
          .from('imagenes_generadas')
          .select('id, file_name')
          .like('file_name', `${newIdStr}.%`)
          .eq('id_company', companyId)
          .maybeSingle();
        if (likeData) newRow = likeData as any;
      }
      if (!newRow?.file_name) {
        throw new Error('No se pudo cargar la nueva imagen editada');
      }

      const newFilePath = `${companyId}/full/${newRow.file_name}`;
      const { data: newSigned } = await supabase.storage.from('generadas').createSignedUrl(newFilePath, 3600);
      if (!newSigned?.signedUrl) {
        throw new Error('No se pudo obtener la URL firmada de la imagen editada');
      }

      // Append to history (truncate any redo states)
      setHistory((prev) => {
        const base = currentIndex >= 0 ? prev.slice(0, currentIndex + 1) : prev;
        return [...base, { id: newRow!.id as string, url: newSigned.signedUrl }];
      });
      setCurrentIndex((prev) => (prev >= 0 ? prev + 1 : 0));

      // Don't clear instruction yet - wait for the image to load
      // Don't show success yet - wait for the image to load
      setIsWaitingForImageLoad(true);

      // On mobile, switch angle_change back to instruction so the <img> renders and fires onLoad
      const isMobile = window.innerWidth < 768;
      if (isMobile && editMode === 'angle_change') {
        setAngleSelection(null);
        setEditMode('instruction');
      }
    } catch (e: any) {
      console.error('Error enviando instrucción de edición:', e);
      setError(e?.message || 'Ocurrió un error al editar la imagen.');
      setIsSubmitting(false);
    }
    // Note: isSubmitting will be set to false when the image loads (in handleImageLoad)
  };

  const handleUndo = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  const handleRedo = () => {
    if (currentIndex < history.length - 1) setCurrentIndex(currentIndex + 1);
  };

  const persistActiveFlags = async () => {
    try {
      if (!companyId) return;
      const ids = Array.from(new Set(history.map((h) => h.id)));
      const activeId = (currentIndex >= 0 && history[currentIndex]
        ? history[currentIndex].id
        : imageId);
      if (ids.length === 0 || !activeId) return;

      // Deactivate all in session
      const { error: deactErr } = await supabase
        .from('imagenes_generadas')
        .update({ active: false })
        .in('id', ids)
        .eq('id_company', companyId);
      if (deactErr) {
        console.error('Error desactivando imágenes:', deactErr);
      }

      // Activate current
      const { error: actErr } = await supabase
        .from('imagenes_generadas')
        .update({ active: true })
        .eq('id', activeId)
        .eq('id_company', companyId);
      if (actErr) {
        console.error('Error activando la imagen actual:', actErr);
      }
    } catch (e) {
      console.error('Error guardando estados activos:', e);
    }
  };

  // Helper: navigate back to the video page when we came from ConfigurarVideoInfluencerVideo
  const navigateBackToVideo = (activeImageId: string) => {
    const st = (location.state as any) || {};
    if (!st.returnToVideo || !st.videoPageParams) return false;
    const { ids, style, companyId: vCompanyId } = st.videoPageParams;
    const qp = new URLSearchParams();
    if (ids) qp.set('ids', ids);
    if (style) qp.set('style', style);
    qp.set('imageId', activeImageId);
    if (vCompanyId) qp.set('companyId', vCompanyId);
    navigate(`/redes-sociales/crear-anuncio/configurar-video-influencer-video?${qp.toString()}`, {
      state: { editedImageId: activeImageId, savedDialogue: st.savedDialogue, savedAccent: st.savedAccent, savedDuration: st.savedDuration },
      replace: true,
    });
    return true;
  };

  // Helper: navigate back to ConfigurarVideoPersonalizado when we came from there
  const navigateBackToVideoPersonalizado = (activeImageId: string) => {
    const st = (location.state as any) || {};
    if (!st.returnToVideoPersonalizado || !st.videoPersonalizadoParams) return false;
    const { ids } = st.videoPersonalizadoParams;
    const qp = new URLSearchParams();
    if (ids) qp.set('ids', ids);
    navigate(`/redes-sociales/crear-anuncio/configurar-video-personalizado?${qp.toString()}`, {
      state: { editedImageId: activeImageId, videoConfig: st.videoConfig },
      replace: true,
    });
    return true;
  };

  const handleSaveAndExit = async () => {
    try {
      setIsSaving(true);
      // Show the same loading animation used on initial mount
      setLoading(true);
      await persistActiveFlags();
    } finally {
      setIsSaving(false);
      const st = (location.state as any) || {};
      if (st.returnToResults && Array.isArray(st.resultsImageIds)) {
        const activeId = currentIndex >= 0 && history[currentIndex]
          ? history[currentIndex].id
          : imageId;
        const originalList = (st.resultsImageIds as any[]).map((x) => String(x));
        const sessionIds = Array.from(new Set(history.map((h) => h.id)));

        let updatedIds: string[] = originalList.slice();
        if (sessionIds.length > 0) {
          const firstSessionIdx = originalList.findIndex((id: string) => sessionIds.includes(id));
          const filtered = originalList.filter((id: string) => !sessionIds.includes(id));
          const insertAt = firstSessionIdx >= 0 ? Math.min(firstSessionIdx, filtered.length) : filtered.length;

          if (filtered.includes(activeId)) {
            filtered.splice(filtered.indexOf(activeId), 1);
          }
          filtered.splice(insertAt, 0, activeId);
          updatedIds = filtered;
        } else if (!updatedIds.includes(activeId)) {
          updatedIds = [...updatedIds, activeId];
        }

        const uniqueUpdatedIds = updatedIds.filter((id, index, arr) => arr.indexOf(id) === index);
        // Convert IDs back to numbers for GeneratedImagesInline compatibility
        const numericIds = uniqueUpdatedIds.map(id => {
          const num = Number(id);
          return isNaN(num) ? id : num; // Keep as-is if not a valid number
        });
        // Update cached images: remove old session images and add the new edited one
        let updatedCachedImages: any[] = [];
        if (Array.isArray(st.cachedImages)) {
          // Filter out images that were part of the edit session (replaced by new versions)
          updatedCachedImages = st.cachedImages.filter((img: any) => 
            !sessionIds.includes(String(img.id))
          );
          // Add the new edited image if we have a URL
          if (imageUrl && activeId) {
            // Find the original image to get fileName (or use a placeholder)
            const originalImg = st.cachedImages.find((img: any) => String(img.id) === imageId);
            // Try to convert activeId to number, keep as string if not valid
            const activeIdNum = Number(activeId);
            const finalActiveId = isNaN(activeIdNum) ? activeId : activeIdNum;
            updatedCachedImages.push({
              id: finalActiveId,
              fileName: originalImg?.fileName || '',
              signedUrl: imageUrl,
              favorito: originalImg?.favorito || false,
            });
          }
        }
        navigate('/redes-sociales/imagenes-generadas', {
          state: {
            imageIds: numericIds,
            companyId: st.resultsCompanyId,
            preloadedImages: updatedCachedImages.length > 0 ? updatedCachedImages : undefined,
          },
          replace: true,
        });
        return;
      }
      // Return to video page if we came from there
      const saveActiveId = currentIndex >= 0 && history[currentIndex] ? history[currentIndex].id : imageId;
      if (navigateBackToVideo(saveActiveId)) return;
      if (navigateBackToVideoPersonalizado(saveActiveId)) return;
      navigate(-1);
    }
  };

  // Save as new image: keep the original active, only activate the new edited image
  const handleSaveAsNewAndExit = async () => {
    try {
      setIsSaving(true);
      setLoading(true);

      if (companyId) {
        const activeId = (currentIndex >= 0 && history[currentIndex]
          ? history[currentIndex].id
          : imageId);
        if (activeId && activeId !== imageId) {
          // Only activate the new edited image, don't deactivate the original
          const { error: actErr } = await supabase
            .from('imagenes_generadas')
            .update({ active: true })
            .eq('id', activeId)
            .eq('id_company', companyId);
          if (actErr) {
            console.error('Error activando la nueva imagen:', actErr);
          }

          // Deactivate intermediate edits (not the original and not the current)
          const intermediateIds = Array.from(new Set(history.map((h) => h.id)))
            .filter(id => id !== imageId && id !== activeId);
          if (intermediateIds.length > 0) {
            const { error: deactErr } = await supabase
              .from('imagenes_generadas')
              .update({ active: false })
              .in('id', intermediateIds)
              .eq('id_company', companyId);
            if (deactErr) {
              console.error('Error desactivando imágenes intermedias:', deactErr);
            }
          }
        }
      }
    } finally {
      setIsSaving(false);
      const st = (location.state as any) || {};
      if (st.returnToResults && Array.isArray(st.resultsImageIds)) {
        const activeId = currentIndex >= 0 && history[currentIndex]
          ? history[currentIndex].id
          : imageId;
        const originalList = (st.resultsImageIds as any[]).map((x) => String(x));

        // Keep original in list and add the new image after it
        let updatedIds: string[] = originalList.slice();
        if (!updatedIds.includes(activeId)) {
          const originalIdx = updatedIds.indexOf(imageId);
          if (originalIdx >= 0) {
            updatedIds.splice(originalIdx + 1, 0, activeId);
          } else {
            updatedIds.push(activeId);
          }
        }

        const uniqueUpdatedIds = updatedIds.filter((id, index, arr) => arr.indexOf(id) === index);
        const numericIds = uniqueUpdatedIds.map(id => {
          const num = Number(id);
          return isNaN(num) ? id : num;
        });

        let updatedCachedImages: any[] = [];
        if (Array.isArray(st.cachedImages)) {
          updatedCachedImages = [...st.cachedImages];
          if (imageUrl && activeId) {
            const originalImg = st.cachedImages.find((img: any) => String(img.id) === imageId);
            const activeIdNum = Number(activeId);
            const finalActiveId = isNaN(activeIdNum) ? activeId : activeIdNum;
            updatedCachedImages.push({
              id: finalActiveId,
              fileName: originalImg?.fileName || '',
              signedUrl: imageUrl,
              favorito: originalImg?.favorito || false,
            });
          }
        }
        navigate('/redes-sociales/imagenes-generadas', {
          state: {
            imageIds: numericIds,
            companyId: st.resultsCompanyId,
            preloadedImages: updatedCachedImages.length > 0 ? updatedCachedImages : undefined,
          },
          replace: true,
        });
        return;
      }
      // Return to video page if we came from there
      const saveNewActiveId = currentIndex >= 0 && history[currentIndex] ? history[currentIndex].id : imageId;
      if (navigateBackToVideo(saveNewActiveId)) return;
      if (navigateBackToVideoPersonalizado(saveNewActiveId)) return;
      navigate(-1);
    }
  };


  // Cancel without saving: return to results if we came from there, preserving original list
  const handleCancelRef = useRef<() => void>(() => {});
  const handleCancel = () => {
    const st = (location.state as any) || {};
    if (st.returnToResults && Array.isArray(st.resultsImageIds)) {
      setLoading(true);
      navigate('/redes-sociales/imagenes-generadas', {
        state: {
          imageIds: st.resultsImageIds,
          companyId: st.resultsCompanyId,
          preloadedImages: st.cachedImages,
        },
        replace: true,
      });
      return;
    }
    setLoading(true);
    // Return to video page if we came from there (cancel = keep original imageId)
    if (navigateBackToVideo(imageId)) return;
    if (navigateBackToVideoPersonalizado(imageId)) return;
    navigate(-1);
  };
  handleCancelRef.current = handleCancel;

  // Override mobile back button to use handleCancel (preserves dialogue state when returning to video page)
  useEffect(() => {
    setMobileBackOverride(() => () => handleCancelRef.current());
    return () => setMobileBackOverride(null);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="w-full bg-surface-bg min-h-[100dvh] md:min-h-0">
      {/* Desktop sticky header — Apple frosted glass nav bar */}
      <div className="hidden md:block fixed top-0 right-0 z-40" style={{ left: sidebarWidth }}>
        <div className="bg-white border-b border-black/[0.06]">
          <div className="max-w-6xl mx-auto px-8">
            <div className="flex items-center justify-between h-14">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="inline-flex items-center gap-1 text-[#1644EB] hover:opacity-70 transition-opacity text-[15px] font-normal"
                >
                  <ChevronLeft className="w-5 h-5" />
                  <span>Atrás</span>
                </button>
              </div>
              <h1 className="absolute left-1/2 -translate-x-1/2 text-[17px] font-semibold text-brand-black/90">Editar imagen</h1>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="inline-flex items-center text-[#1644EB] hover:opacity-70 transition-opacity text-[15px] font-normal"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop layout: fixed positioning like GeneratedImagesInline */}
      <div className="hidden md:flex md:flex-row md:items-start md:justify-center fixed top-[58px] bottom-0 right-0 z-30 bg-surface-bg px-4 pt-4 gap-4" style={{ left: sidebarWidth }}>
        {/* Left: image viewer — flex-shrink-0, image drives width */}
        <div className="flex-shrink-0 flex items-center justify-center relative" style={{ height: 'calc(100vh - 8rem)' }}>
          {loading ? (
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
          ) : imageUrl ? (
            <div ref={imageContainerRef} className="relative flex items-center justify-center h-full">
              {(isSubmitting || isWaitingForImageLoad || showEditSuccess) && (
                <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-surface-card/90 backdrop-blur-sm border border-neutral-200 rounded-full shadow-sm">
                    {(isSubmitting || isWaitingForImageLoad) ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin text-brand-primary" />
                        <span className="text-[13px] font-medium text-neutral-700">Editando imagen</span>
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 text-green-600" />
                        <span className="text-[13px] font-medium text-neutral-700">Imagen editada</span>
                      </>
                    )}
                  </div>
                </div>
              )}
              <img 
                ref={imageRef}
                src={imageUrl} 
                alt="Imagen a editar" 
                style={{ height: 'calc(100vh - 8rem)', width: 'auto', maxWidth: 'calc(100vw - 500px)' }} 
                className="object-contain rounded-md" 
                onLoad={(e) => handleImageLoad(e, false)}
              />
              {/* Canvas overlay for precise edit mode */}
              {editMode === 'precise' && (
                <canvas
                  ref={maskCanvasRef}
                  className="absolute top-0 left-0 w-full h-full rounded-md cursor-crosshair"
                  style={{ pointerEvents: 'auto', opacity: 0.4, touchAction: 'none' }}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
              )}
            </div>
          ) : (
            <div className="text-neutral-500 text-[15px]">Imagen no disponible</div>
          )}
        </div>

        {/* Right panel */}
        <div className="flex-1 min-w-[33%] max-w-[50%] bg-surface-card flex flex-col rounded-md border border-neutral-200" style={{ height: 'calc(100vh - 8rem)' }}>
          {/* Error banner */}
          {error && (
            <div className="mx-4 mt-3 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-sm text-[13px]">{error}</div>
          )}

          {/* Content */}
          {loading ? (
            <div className="flex-1 p-8 flex flex-col items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mb-4"></div>
              <h3 className="text-[17px] font-semibold text-brand-black/90">Cargando...</h3>
              <p className="text-neutral-500 mt-2 text-[15px]">Este proceso puede tardar unos momentos.</p>
            </div>
          ) : (
            <div className="flex-1 flex flex-col justify-end p-4 overflow-hidden">
              {/* Clear mask button when in precise mode with mask */}
              {editMode === 'precise' && hasMask && (
                <div className="flex justify-end mb-2">
                  <button
                    type="button"
                    onClick={clearMask}
                    disabled={isSubmitting}
                    className="h-8 px-3 bg-red-100 text-red-600 hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-sm text-[13px] font-normal inline-flex items-center gap-2"
                  >
                    <Eraser className="w-4 h-4" />
                    Limpiar selección
                  </button>
                </div>
              )}
              {/* Angle change 3D selector (desktop) */}
              {editMode === 'angle_change' && (
                <div className="flex-1 min-h-0 mb-3 rounded-md overflow-hidden border border-neutral-200">
                  <AngleSelector3D
                    imageUrl={imageUrl}
                    onSelectionChange={(sel) => setAngleSelection(sel)}
                    disabled={isSubmitting}
                  />
                </div>
              )}
              {/* Mode illustration (desktop, before first edit) */}
              {history.length <= 1 && (editMode === 'instruction' || editMode === 'precise' || editMode === 'product_change') && (
                <div className="flex-1 flex flex-col items-center justify-center min-h-0 mb-3">
                  <img
                    src={editMode === 'instruction' ? '/images/editar_prompt.png' : editMode === 'precise' ? '/images/editar_inline.png' : '/images/editar_cambio_producto.png'}
                    alt={editMode === 'instruction' ? 'Edición con instrucción' : editMode === 'precise' ? 'Edición precisa' : 'Cambio de producto'}
                    className="max-h-[55%] w-auto object-contain"
                    draggable={false}
                  />
                  <p className="text-[13px] text-neutral-500 text-center mt-3 max-w-[280px]">
                    {editMode === 'instruction'
                      ? 'Describí con texto el cambio que querés hacer en la imagen y Atlas lo aplicará.'
                      : editMode === 'precise'
                      ? 'Marcá sobre la imagen el área que querés modificar y luego describí el cambio.'
                      : 'Seleccioná un producto de tu catálogo para reemplazarlo en la imagen.'}
                  </p>
                </div>
              )}

              {/* Text field container */}
              <div className={`flex flex-col bg-surface-elevated border border-neutral-200 rounded-lg ${isSubmitting ? 'shine-effect' : ''} focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-brand-primary`}>
                <div className="relative min-h-[44px]">
                  {/* Show message when in precise mode without mask */}
                  {editMode === 'precise' && !hasMask ? (
                    <div className="flex items-center gap-3 px-4 py-3 text-neutral-600">
                      <div className="w-8 h-8 rounded-sm bg-neutral-100 flex items-center justify-center flex-shrink-0">
                        <MousePointer2 className="w-4 h-4 text-neutral-600" />
                      </div>
                      <span className="text-[15px]">Marcá en la imagen el área donde quisieras realizar el cambio</span>
                    </div>
                  ) : editMode === 'angle_change' ? (
                    <div className="flex items-center gap-3 px-4 py-3">
                      <div className="w-8 h-8 rounded-sm bg-neutral-100 flex items-center justify-center flex-shrink-0">
                        <Camera className="w-4 h-4 text-neutral-600" />
                      </div>
                      <span className={`text-[15px] ${angleSelection ? 'text-neutral-400' : 'text-neutral-600'}`}>
                        {angleSelection
                          ? `Ángulo: ${angleSelection.horizontal_angle}° horizontal · ${angleSelection.vertical_angle}° vertical · Zoom ${angleSelection.zoom > 5 ? '+' : ''}${angleSelection.zoom - 5}`
                          : 'Seleccioná un punto en la vista 3D para elegir el ángulo de cámara'}
                      </span>
                    </div>
                  ) : editMode === 'product_change' ? (
                    <div className="px-4 py-3">
                      <p className="text-[15px] text-neutral-500 mb-3">
                        {selectedProduct 
                          ? 'El producto seleccionado se insertará en la imagen'
                          : 'Seleccioná el producto que querés insertar en la imagen'}
                      </p>
                      {selectedProduct ? (
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setShowProductPicker(true)}
                            disabled={isSubmitting}
                            className="flex items-center gap-3 p-2 bg-surface-card border border-neutral-200 rounded-sm hover:border-neutral-300 transition-colors"
                          >
                            {selectedProduct.imageUrl && (
                              <img src={selectedProduct.imageUrl} alt={selectedProduct.nombre} className="w-12 h-12 rounded-sm object-cover flex-shrink-0" />
                            )}
                            <span className="text-[15px] font-medium text-brand-black/90 pr-1">{selectedProduct.nombre}</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setSelectedProduct(null)}
                            disabled={isSubmitting}
                            className="p-1.5 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 disabled:opacity-50 disabled:cursor-not-allowed rounded-sm transition-colors"
                            aria-label="Quitar producto"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setShowProductPicker(true)}
                          disabled={isSubmitting}
                          className="h-9 px-4 bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-50 text-white rounded-md text-[15px] font-medium shadow-sm transition-colors"
                        >
                          Seleccionar producto
                        </button>
                      )}
                    </div>
                  ) : (
                    <textarea
                      ref={desktopInputRef}
                      value={instruction}
                      onChange={(e) => {
                        setInstruction(e.target.value);
                        autoResizeTextarea(e.target);
                      }}
                      placeholder="Describí el cambio que querés hacer..."
                      disabled={isSubmitting}
                      rows={1}
                      className="w-full pl-4 pr-4 bg-transparent focus:outline-none border-none text-[15px] placeholder:text-neutral-400 disabled:text-neutral-500 disabled:cursor-not-allowed resize-none overflow-hidden whitespace-pre-wrap"
                      style={{
                        lineHeight: '1.25rem',
                        paddingTop: '0.75rem',
                        paddingBottom: '0.5rem',
                      }}
                    />
                  )}
                </div>
                <div className="flex justify-between items-center px-2 pb-2">
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowEditModeMenu(!showEditModeMenu)}
                      disabled={isSubmitting}
                      className={`h-9 px-3 rounded-md font-normal shadow-sm inline-flex items-center justify-center gap-2 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''} ${showEditModeMenu ? 'bg-brand-primary text-white hover:bg-brand-primary-hover border border-transparent' : 'bg-surface-card text-neutral-600 border border-neutral-200 hover:bg-surface-elevated'}`}
                    >
                      <Plus className="w-4 h-4" />
                      <span className="text-[15px]">Más</span>
                    </button>
                    {showEditModeMenu && (
                      <>
                        <div className="fixed inset-0 z-[5]" onClick={() => setShowEditModeMenu(false)} />
                        <div className="absolute bottom-full left-0 mb-2 bg-surface-card rounded-[14px] border border-neutral-200 shadow-lg p-3 z-10 min-w-[280px]">
                          <button
                            type="button"
                            onClick={() => { setEditMode('instruction'); setShowEditModeMenu(false); }}
                            className={`flex items-start gap-3 w-full p-3 rounded-sm transition-colors text-left ${editMode === 'instruction' ? 'bg-neutral-100' : 'hover:bg-surface-elevated'}`}
                          >
                            <div className={`w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0 ${editMode === 'instruction' ? 'bg-brand-primary text-white' : 'bg-neutral-100 text-neutral-600'}`}>
                              <Pencil className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-[15px] font-medium ${editMode === 'instruction' ? 'text-brand-black/90' : 'text-brand-black/90'}`}>Edición con instrucción</p>
                              <p className="text-[13px] text-neutral-500 mt-0.5">Describí el cambio que querés hacer</p>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() => { setEditMode('precise'); setShowEditModeMenu(false); }}
                            className={`flex items-start gap-3 w-full p-3 rounded-sm transition-colors text-left mt-1 ${editMode === 'precise' ? 'bg-neutral-100' : 'hover:bg-surface-elevated'}`}
                          >
                            <div className={`w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0 ${editMode === 'precise' ? 'bg-brand-primary text-white' : 'bg-neutral-100 text-neutral-600'}`}>
                              <MousePointer2 className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-[15px] font-medium ${editMode === 'precise' ? 'text-brand-black/90' : 'text-brand-black/90'}`}>Edición precisa</p>
                              <p className="text-[13px] text-neutral-500 mt-0.5">Seleccioná un área específica para editar</p>
                            </div>
                          </button>
                          {/* Temporarily hidden - Borrador option
                          <button
                            type="button"
                            onClick={() => { setEditMode('eraser'); setShowEditModeMenu(false); }}
                            className={`flex items-start gap-3 w-full p-3 rounded-sm transition-colors text-left mt-1 ${editMode === 'eraser' ? 'bg-neutral-100' : 'hover:bg-surface-elevated'}`}
                          >
                            <div className={`w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0 ${editMode === 'eraser' ? 'bg-brand-primary text-white' : 'bg-neutral-100 text-neutral-600'}`}>
                              <Eraser className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-[15px] font-medium ${editMode === 'eraser' ? 'text-brand-black/90' : 'text-brand-black/90'}`}>Borrador</p>
                              <p className="text-[13px] text-neutral-500 mt-0.5">Eliminá elementos de la imagen</p>
                            </div>
                          </button>
                          */}
                          <button
                            type="button"
                            onClick={() => { setEditMode('product_change'); setShowEditModeMenu(false); }}
                            className={`flex items-start gap-3 w-full p-3 rounded-sm transition-colors text-left mt-1 ${editMode === 'product_change' ? 'bg-neutral-100' : 'hover:bg-surface-elevated'}`}
                          >
                            <div className={`w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0 ${editMode === 'product_change' ? 'bg-brand-primary text-white' : 'bg-neutral-100 text-neutral-600'}`}>
                              <RefreshCw className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-[15px] font-medium ${editMode === 'product_change' ? 'text-brand-black/90' : 'text-brand-black/90'}`}>Cambio de producto</p>
                              <p className="text-[13px] text-neutral-500 mt-0.5">Reemplazá el producto en la imagen</p>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() => { setEditMode('angle_change'); setAngleSelection(null); setShowEditModeMenu(false); }}
                            className={`flex items-start gap-3 w-full p-3 rounded-sm transition-colors text-left mt-1 ${editMode === 'angle_change' ? 'bg-neutral-100' : 'hover:bg-surface-elevated'}`}
                          >
                            <div className={`w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0 ${editMode === 'angle_change' ? 'bg-brand-primary text-white' : 'bg-neutral-100 text-neutral-600'}`}>
                              <Camera className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-[15px] font-medium ${editMode === 'angle_change' ? 'text-brand-black/90' : 'text-brand-black/90'}`}>Cambiar ángulo</p>
                              <p className="text-[13px] text-neutral-500 mt-0.5">Cambiá el ángulo de cámara de la imagen</p>
                            </div>
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                  {editMode === 'product_change' ? (
                    selectedProduct && (
                      <button
                        type="button"
                        onClick={handleSendInstruction}
                        disabled={isSubmitting}
                        className="h-9 w-9 bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-50 text-white rounded-md font-normal shadow-sm inline-flex items-center justify-center"
                        aria-label="Enviar"
                      >
                        <ArrowUp className="w-4 h-4" />
                      </button>
                    )
                  ) : editMode === 'angle_change' ? (
                    angleSelection && (
                      <button
                        type="button"
                        onClick={handleSendInstruction}
                        disabled={isSubmitting}
                        className="h-9 w-9 bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-50 text-white rounded-md font-normal shadow-sm inline-flex items-center justify-center"
                        aria-label="Enviar"
                      >
                        <ArrowUp className="w-4 h-4" />
                      </button>
                    )
                  ) : (
                    <button
                      type="button"
                      onClick={handleSendInstruction}
                      disabled={isSubmitting || !instruction.trim()}
                      className="h-9 w-9 bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-50 text-white rounded-md font-normal shadow-sm inline-flex items-center justify-center"
                      aria-label="Enviar instrucción"
                    >
                      <ArrowUp className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Botones debajo del text field - solo visibles después de al menos una edición */}
              {history.length > 1 && (
                <div className="flex items-center gap-2 mt-3 flex-shrink-0">
                  <button
                    type="button"
                    onClick={handleUndo}
                    disabled={currentIndex <= 0 || isSubmitting}
                    className="flex-1 h-10 px-3 bg-surface-card text-neutral-600 border border-neutral-200 hover:bg-surface-elevated disabled:opacity-60 disabled:cursor-not-allowed rounded-md font-normal shadow-sm inline-flex items-center justify-center gap-2 text-[15px]"
                    aria-label="Deshacer"
                  >
                    <Undo2 className="w-4 h-4" />
                    Deshacer
                  </button>
                  <button
                    type="button"
                    onClick={handleRedo}
                    disabled={currentIndex >= history.length - 1 || isSubmitting}
                    className="flex-1 h-10 px-3 bg-surface-card text-neutral-600 border border-neutral-200 hover:bg-surface-elevated disabled:opacity-60 disabled:cursor-not-allowed rounded-md font-normal shadow-sm inline-flex items-center justify-center gap-2 text-[15px]"
                    aria-label="Rehacer"
                  >
                    <Redo2 className="w-4 h-4" />
                    Rehacer
                  </button>
                  <div className="flex-1 relative">
                    <div className="flex h-10 rounded-md overflow-hidden shadow-sm">
                      <button
                        type="button"
                        onClick={handleSaveAndExit}
                        disabled={isSaving || !hasEdits || isSubmitting}
                        className="flex-1 h-full px-3 bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-normal inline-flex items-center justify-center gap-2 rounded-l-md text-[15px]"
                      >
                        <Save className="w-4 h-4" />
                        Guardar
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowSaveMenu(!showSaveMenu)}
                        disabled={isSaving || !hasEdits || isSubmitting}
                        className="h-full px-2 bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-50 disabled:cursor-not-allowed text-white border-l border-white/20 rounded-r-md"
                      >
                        <ChevronDown className="w-4 h-4" />
                      </button>
                    </div>
                    {showSaveMenu && (
                      <>
                        <div className="fixed inset-0 z-[5]" onClick={() => setShowSaveMenu(false)} />
                        <div className="absolute bottom-full left-0 right-0 mb-2 bg-surface-card rounded-[14px] border border-neutral-200 shadow-lg p-2 z-10 min-w-[220px]">
                          <button
                            type="button"
                            onClick={() => { setShowSaveMenu(false); handleSaveAndExit(); }}
                            className="flex items-center gap-3 w-full p-2.5 rounded-sm hover:bg-surface-elevated text-left"
                          >
                            <Save className="w-4 h-4 text-neutral-600" />
                            <div>
                              <p className="text-[15px] font-medium text-brand-black/90">Guardar</p>
                              <p className="text-[13px] text-neutral-500">Reemplaza la imagen original</p>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() => { setShowSaveMenu(false); handleSaveAsNewAndExit(); }}
                            className="flex items-center gap-3 w-full p-2.5 rounded-sm hover:bg-surface-elevated text-left mt-1"
                          >
                            <Copy className="w-4 h-4 text-neutral-600" />
                            <div>
                              <p className="text-[15px] font-medium text-brand-black/90">Guardar como nueva</p>
                              <p className="text-[13px] text-neutral-500">Conserva la imagen original</p>
                            </div>
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              <div className="mt-3 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setShowHelpPopup(true)}
                  className="text-[13px] text-neutral-400 hover:text-neutral-500 underline"
                >
                  {hasEdits ? '¿No se modificó la foto que editaste?' : '¿El producto se ve diferente al original?'}
                </button>
                <p className="text-[13px] text-neutral-500">Cada edición consume 1 imagen.</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile layout */}
      <div className="md:hidden fixed inset-0 bg-surface-bg flex flex-col z-40 overflow-hidden px-4 pt-16">
        {/* Error banner */}
        {error && (
          <div className="mt-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-sm text-[13px] flex-shrink-0">{error}</div>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex-1 flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mb-4"></div>
            <h3 className="text-[17px] font-semibold text-brand-black/90">Cargando...</h3>
            <p className="text-neutral-500 mt-2 text-[15px]">Este proceso puede tardar unos momentos.</p>
          </div>
        ) : editMode === 'angle_change' ? (
          <div className="flex-1 flex flex-col overflow-hidden min-h-0 mt-2">
            {/* 3D Angle Selector (mobile - replaces image view) */}
            {(isSubmitting || isWaitingForImageLoad || showEditSuccess) && (
              <div className="flex justify-center mb-2">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-surface-card/90 backdrop-blur-sm border border-neutral-200 rounded-full shadow-sm">
                  {(isSubmitting || isWaitingForImageLoad) ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin text-brand-primary" />
                      <span className="text-[13px] font-medium text-neutral-700">Editando imagen</span>
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 text-green-600" />
                      <span className="text-[13px] font-medium text-neutral-700">Imagen editada</span>
                    </>
                  )}
                </div>
              </div>
            )}
            <div className="flex-1 min-h-0 rounded-md overflow-hidden border border-neutral-200">
              <AngleSelector3D
                imageUrl={imageUrl}
                onSelectionChange={(sel) => setAngleSelection(sel)}
                disabled={isSubmitting}
              />
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col justify-center items-center overflow-hidden min-h-0">
            {/* Imagen */}
            {imageUrl ? (
              <div className="relative flex items-center justify-center max-h-full">
                {(isSubmitting || isWaitingForImageLoad || showEditSuccess) && (
                  <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-surface-card/90 backdrop-blur-sm border border-neutral-200 rounded-full shadow-sm whitespace-nowrap">
                      {(isSubmitting || isWaitingForImageLoad) ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin text-brand-primary" />
                          <span className="text-[13px] font-medium text-neutral-700">Editando imagen</span>
                        </>
                      ) : (
                        <>
                          <Check className="w-4 h-4 text-green-600" />
                          <span className="text-[13px] font-medium text-neutral-700">Imagen editada</span>
                        </>
                      )}
                    </div>
                  </div>
                )}
                <img 
                  ref={imageMobileRef}
                  src={imageUrl} 
                  alt="Imagen a editar" 
                  className="max-w-full max-h-full object-contain rounded-md" 
                  onLoad={(e) => handleImageLoad(e, true)}
                />
                {/* Canvas overlay for precise edit mode (mobile) */}
                {editMode === 'precise' && (
                  <canvas
                    ref={maskCanvasMobileRef}
                    className="absolute top-0 left-0 w-full h-full rounded-md cursor-crosshair"
                    style={{ pointerEvents: 'auto', opacity: 0.4, touchAction: 'none' }}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                  />
                )}
              </div>
            ) : (
              <div className="text-neutral-500 text-[15px]">Imagen no disponible</div>
            )}
          </div>
        )}

        {/* Controles (mobile) */}
        <div
          className="bg-surface-bg pt-2 space-y-2 flex-shrink-0 -mx-4 px-4"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
        >
          {/* Clear mask button when in precise mode with mask (mobile) */}
          {editMode === 'precise' && hasMask && (
            <div className="flex justify-end mb-1">
              <button
                type="button"
                onClick={clearMask}
                disabled={isSubmitting}
                className="h-8 px-3 bg-red-100 text-red-600 hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-sm text-[13px] font-normal inline-flex items-center gap-2"
              >
                <Eraser className="w-4 h-4" />
                Limpiar selección
              </button>
            </div>
          )}
          <div className={`flex flex-col bg-white border border-neutral-200 rounded-lg ${isSubmitting ? 'shine-effect' : ''} focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-brand-primary`}>
            <div className="relative min-h-[44px]">
              {/* Show message when in precise mode without mask (mobile) */}
              {editMode === 'precise' && !hasMask ? (
                <div className="flex items-center gap-3 px-4 py-3 text-neutral-600">
                  <div className="w-8 h-8 rounded-sm bg-neutral-100 flex items-center justify-center flex-shrink-0">
                    <Hand className="w-4 h-4 text-neutral-600" />
                  </div>
                  <span className="text-[15px]">Marcá en la imagen el área donde quisieras realizar el cambio</span>
                </div>
              ) : editMode === 'angle_change' ? (
                <div className="flex items-center gap-3 px-4 py-3">
                  <div className="w-8 h-8 rounded-sm bg-neutral-100 flex items-center justify-center flex-shrink-0">
                    <Camera className="w-4 h-4 text-neutral-600" />
                  </div>
                  <span className={`text-[15px] ${angleSelection ? 'text-neutral-400' : 'text-neutral-600'}`}>
                    {angleSelection
                      ? `Ángulo: ${angleSelection.horizontal_angle}° horizontal · ${angleSelection.vertical_angle}° vertical · Zoom ${angleSelection.zoom > 5 ? '+' : ''}${angleSelection.zoom - 5}`
                      : 'Seleccioná un punto en la vista 3D para elegir el ángulo de cámara'}
                  </span>
                </div>
              ) : editMode === 'product_change' ? (
                <div className="px-4 py-3">
                  <p className="text-[15px] text-neutral-500 mb-3">
                    {selectedProduct 
                      ? 'El producto seleccionado se insertará en la imagen'
                      : 'Seleccioná el producto que querés insertar en la imagen'}
                  </p>
                  {selectedProduct ? (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setShowProductPicker(true)}
                        disabled={isSubmitting}
                        className="flex items-center gap-3 p-2 bg-surface-card border border-neutral-200 rounded-sm hover:border-neutral-300 transition-colors"
                      >
                        {selectedProduct.imageUrl && (
                          <img src={selectedProduct.imageUrl} alt={selectedProduct.nombre} className="w-12 h-12 rounded-sm object-cover flex-shrink-0" />
                        )}
                        <span className="text-[15px] font-medium text-brand-black/90 pr-1">{selectedProduct.nombre}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedProduct(null)}
                        disabled={isSubmitting}
                        className="p-1.5 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 disabled:opacity-50 disabled:cursor-not-allowed rounded-sm transition-colors"
                        aria-label="Quitar producto"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowProductPicker(true)}
                      disabled={isSubmitting}
                      className="h-9 px-4 bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-50 text-white rounded-md text-[15px] font-medium shadow-sm transition-colors"
                    >
                      Seleccionar producto
                    </button>
                  )}
                </div>
              ) : (
                <textarea
                  value={instruction}
                  onChange={(e) => {
                    setInstruction(e.target.value);
                    autoResizeTextarea(e.target);
                  }}
                  placeholder="Describí el cambio que querés hacer..."
                  disabled={isSubmitting}
                  rows={1}
                  className="w-full pl-4 pr-4 bg-transparent focus:outline-none border-none text-[15px] placeholder:text-neutral-400 disabled:text-neutral-500 disabled:cursor-not-allowed resize-none overflow-hidden whitespace-pre-wrap"
                  style={{
                    lineHeight: '1.25rem',
                    paddingTop: '0.75rem',
                    paddingBottom: '0.5rem',
                  }}
                />
              )}
            </div>
            <div className="flex justify-between items-center px-2 pb-2">
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowEditModeMenu(!showEditModeMenu)}
                  disabled={isSubmitting}
                  className={`h-9 px-3 rounded-md font-normal shadow-sm inline-flex items-center justify-center gap-2 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''} ${showEditModeMenu ? 'bg-brand-primary text-white hover:bg-brand-primary-hover border border-transparent' : 'bg-surface-card text-neutral-600 border border-neutral-200 hover:bg-surface-elevated'}`}
                >
                  <Plus className="w-4 h-4" />
                  <span className="text-[15px]">Más</span>
                </button>
                    {showEditModeMenu && (
                      <>
                        <div className="fixed inset-0 z-[5]" onClick={() => setShowEditModeMenu(false)} />
                        <div className="absolute bottom-full left-0 mb-2 bg-surface-card rounded-[14px] border border-neutral-200 shadow-lg p-3 z-10 min-w-[280px]">
                          <button
                            type="button"
                            onClick={() => { setEditMode('instruction'); setShowEditModeMenu(false); }}
                            className={`flex items-start gap-3 w-full p-3 rounded-sm transition-colors text-left ${editMode === 'instruction' ? 'bg-neutral-100' : 'hover:bg-surface-elevated'}`}
                          >
                            <div className={`w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0 ${editMode === 'instruction' ? 'bg-brand-primary text-white' : 'bg-neutral-100 text-neutral-600'}`}>
                              <Pencil className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[15px] font-medium text-brand-black/90">Edición con instrucción</p>
                              <p className="text-[13px] text-neutral-500 mt-0.5">Describí el cambio que querés hacer</p>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() => { setEditMode('precise'); setShowEditModeMenu(false); }}
                            className={`flex items-start gap-3 w-full p-3 rounded-sm transition-colors text-left mt-1 ${editMode === 'precise' ? 'bg-neutral-100' : 'hover:bg-surface-elevated'}`}
                          >
                            <div className={`w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0 ${editMode === 'precise' ? 'bg-brand-primary text-white' : 'bg-neutral-100 text-neutral-600'}`}>
                              <MousePointer2 className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[15px] font-medium text-brand-black/90">Edición precisa</p>
                              <p className="text-[13px] text-neutral-500 mt-0.5">Seleccioná un área específica para editar</p>
                            </div>
                          </button>
                          {/* Temporarily hidden - Borrador option
                          <button
                            type="button"
                            onClick={() => { setEditMode('eraser'); setShowEditModeMenu(false); }}
                            className={`flex items-start gap-3 w-full p-3 rounded-sm transition-colors text-left mt-1 ${editMode === 'eraser' ? 'bg-neutral-100' : 'hover:bg-surface-elevated'}`}
                          >
                            <div className={`w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0 ${editMode === 'eraser' ? 'bg-brand-primary text-white' : 'bg-neutral-100 text-neutral-600'}`}>
                              <Eraser className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[15px] font-medium text-brand-black/90">Borrador</p>
                              <p className="text-[13px] text-neutral-500 mt-0.5">Eliminá elementos de la imagen</p>
                            </div>
                          </button>
                          */}
                          <button
                            type="button"
                            onClick={() => { setEditMode('product_change'); setShowEditModeMenu(false); }}
                            className={`flex items-start gap-3 w-full p-3 rounded-sm transition-colors text-left mt-1 ${editMode === 'product_change' ? 'bg-neutral-100' : 'hover:bg-surface-elevated'}`}
                          >
                            <div className={`w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0 ${editMode === 'product_change' ? 'bg-brand-primary text-white' : 'bg-neutral-100 text-neutral-600'}`}>
                              <RefreshCw className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[15px] font-medium text-brand-black/90">Cambio de producto</p>
                              <p className="text-[13px] text-neutral-500 mt-0.5">Reemplazá el producto en la imagen</p>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() => { setEditMode('angle_change'); setAngleSelection(null); setShowEditModeMenu(false); }}
                            className={`flex items-start gap-3 w-full p-3 rounded-sm transition-colors text-left mt-1 ${editMode === 'angle_change' ? 'bg-neutral-100' : 'hover:bg-surface-elevated'}`}
                          >
                            <div className={`w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0 ${editMode === 'angle_change' ? 'bg-brand-primary text-white' : 'bg-neutral-100 text-neutral-600'}`}>
                              <Camera className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[15px] font-medium text-brand-black/90">Cambiar ángulo</p>
                              <p className="text-[13px] text-neutral-500 mt-0.5">Cambiá el ángulo de cámara de la imagen</p>
                            </div>
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                  {editMode === 'product_change' ? (
                    selectedProduct && (
                      <button
                        type="button"
                        onClick={handleSendInstruction}
                        disabled={isSubmitting}
                        className="h-9 w-9 bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-50 text-white rounded-md font-normal shadow-sm inline-flex items-center justify-center"
                        aria-label="Enviar"
                      >
                        <ArrowUp className="w-4 h-4" />
                      </button>
                    )
                  ) : editMode === 'angle_change' ? (
                    angleSelection && (
                      <button
                        type="button"
                        onClick={handleSendInstruction}
                        disabled={isSubmitting}
                        className="h-9 w-9 bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-50 text-white rounded-md font-normal shadow-sm inline-flex items-center justify-center"
                        aria-label="Enviar"
                      >
                        <ArrowUp className="w-4 h-4" />
                      </button>
                    )
                  ) : (
                    <button
                      type="button"
                      onClick={handleSendInstruction}
                      disabled={isSubmitting || !instruction.trim()}
                      className="h-9 w-9 bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-50 text-white rounded-md font-normal shadow-sm inline-flex items-center justify-center"
                      aria-label="Enviar instrucción"
                    >
                      <ArrowUp className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              {/* Botones solo visibles después de al menos una edición */}
              {history.length > 1 && (() => {
                const canUndo = currentIndex > 0;
                const canRedo = currentIndex < history.length - 1;
                const bothVisible = canUndo && canRedo;
                return (
                <div className="flex gap-2">
                  {canUndo && (
                    <button
                      type="button"
                      onClick={handleUndo}
                      disabled={isSubmitting}
                      className={`h-10 bg-surface-card text-neutral-600 border border-neutral-200 hover:bg-surface-elevated disabled:opacity-60 disabled:cursor-not-allowed rounded-md shadow-sm inline-flex items-center justify-center ${bothVisible ? 'w-10' : 'px-3 gap-2'}`}
                      aria-label="Deshacer"
                    >
                      <Undo2 className="w-4 h-4" />
                      {!bothVisible && <span className="text-[15px]">Deshacer</span>}
                    </button>
                  )}
                  {canRedo && (
                    <button
                      type="button"
                      onClick={handleRedo}
                      disabled={isSubmitting}
                      className={`h-10 bg-surface-card text-neutral-600 border border-neutral-200 hover:bg-surface-elevated disabled:opacity-60 disabled:cursor-not-allowed rounded-md shadow-sm inline-flex items-center justify-center ${bothVisible ? 'w-10' : 'px-3 gap-2'}`}
                      aria-label="Rehacer"
                    >
                      <Redo2 className="w-4 h-4" />
                      {!bothVisible && <span className="text-[15px]">Rehacer</span>}
                    </button>
                  )}
                  <div className="relative flex-1">
                    <div className="flex h-10 rounded-md overflow-hidden shadow-sm">
                      <button
                        type="button"
                        onClick={handleSaveAndExit}
                        disabled={isSaving || !hasEdits || isSubmitting}
                        className="flex-1 h-full px-3 bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-normal inline-flex items-center justify-center gap-2 rounded-l-md text-[15px]"
                      >
                        <Save className="w-4 h-4" />
                        <span className="text-[15px]">Guardar</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowSaveMenu(!showSaveMenu)}
                        disabled={isSaving || !hasEdits || isSubmitting}
                        className="h-full px-2 bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-50 disabled:cursor-not-allowed text-white border-l border-white/20 rounded-r-md"
                      >
                        <ChevronDown className="w-4 h-4" />
                      </button>
                    </div>
                    {showSaveMenu && (
                      <>
                        <div className="fixed inset-0 z-[5]" onClick={() => setShowSaveMenu(false)} />
                        <div className="absolute bottom-full right-0 mb-2 bg-surface-card rounded-[14px] border border-neutral-200 shadow-lg p-2 z-10 w-[220px]">
                          <button
                            type="button"
                            onClick={() => { setShowSaveMenu(false); handleSaveAndExit(); }}
                            className="flex items-center gap-3 w-full p-2.5 rounded-sm hover:bg-surface-elevated text-left"
                          >
                            <Save className="w-4 h-4 text-neutral-600 flex-shrink-0" />
                            <div>
                              <p className="text-[15px] font-medium text-brand-black/90">Guardar</p>
                              <p className="text-[13px] text-neutral-500">Reemplaza la original</p>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() => { setShowSaveMenu(false); handleSaveAsNewAndExit(); }}
                            className="flex items-center gap-3 w-full p-2.5 rounded-sm hover:bg-surface-elevated text-left mt-1"
                          >
                            <Copy className="w-4 h-4 text-neutral-600 flex-shrink-0" />
                            <div>
                              <p className="text-[15px] font-medium text-brand-black/90">Guardar como nueva</p>
                              <p className="text-[13px] text-neutral-500">Conserva la original</p>
                            </div>
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                );
              })()}

              <p className="mt-3 text-[13px] text-neutral-500 text-center">Cada edición consume 1 imagen.</p>

              {/* Help text (mobile) */}
              <button
                type="button"
                onClick={() => setShowHelpPopup(true)}
                className="mt-1 text-[13px] text-neutral-400 hover:text-neutral-500 underline text-center w-full"
              >
                {hasEdits ? '¿No se modificó la foto que editaste?' : '¿El producto se ve diferente al original?'}
              </button>
            </div>
      </div>

      {/* Product Picker Modal */}
      {showProductPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-black/30 p-4">
          <div className="bg-surface-card rounded-md w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col shadow-lg">
            <div className="flex items-center justify-between p-4 border-b border-neutral-200">
              <h3 className="text-[17px] font-semibold text-brand-black/90">Seleccionar producto</h3>
              <button
                type="button"
                onClick={() => setShowProductPicker(false)}
                className="p-2 text-neutral-500 hover:text-neutral-700 rounded-full hover:bg-neutral-100"
                aria-label="Cerrar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {loadingProducts ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="bg-surface-card border border-neutral-200 rounded-md overflow-hidden">
                      <div className="w-full aspect-square bg-neutral-100 animate-pulse" />
                      <div className="p-3">
                        <div className="h-4 bg-neutral-100 rounded w-2/3 animate-pulse" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : products.length === 0 ? (
                <div className="text-center py-12">
                  <ImageIcon className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
                  <p className="text-neutral-600 text-[15px]">No tenés productos cargados.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {products.map((product) => (
                    <button
                      key={product.id}
                      type="button"
                      onClick={() => {
                        setSelectedProduct(product);
                        setShowProductPicker(false);
                      }}
                      className={`relative text-left bg-surface-card border rounded-md overflow-hidden focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary group transition-all ${
                        selectedProduct?.id === product.id ? 'ring-2 ring-brand-primary border-brand-primary' : 'border-neutral-200 hover:border-neutral-300'
                      }`}
                    >
                      <div className="relative">
                        {product.imageUrl ? (
                          <img
                            src={product.imageUrl}
                            alt={product.nombre}
                            loading="lazy"
                            className="w-full aspect-square object-cover"
                          />
                        ) : (
                          <div className="w-full aspect-square bg-neutral-100 flex items-center justify-center">
                            <ImageIcon className="w-8 h-8 text-neutral-400" />
                          </div>
                        )}
                        {selectedProduct?.id === product.id && (
                          <div className="absolute top-2 right-2 w-6 h-6 bg-brand-primary rounded-full flex items-center justify-center">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </div>
                      <div className="p-3">
                        <p className="text-[15px] text-brand-black/90 truncate font-medium">{product.nombre}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Moderation error message */}
      {showModerationError && (
        <div className="fixed inset-0 bg-brand-black/30 z-50 flex items-center justify-center p-4">
          <div className="bg-surface-card w-full max-w-sm rounded-md shadow-lg p-6 relative text-center">
            <button
              type="button"
              onClick={() => setShowModerationError(false)}
              className="absolute top-3 right-3 p-2 text-neutral-500 hover:text-neutral-700 rounded-sm focus:outline-none"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
              <X className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-[17px] font-semibold text-brand-black/90 mb-2">No se puede utilizar la función de "Edición precisa" con este tipo de productos. Lo sentimos.</h3>
            <button
              type="button"
              onClick={() => setShowModerationError(false)}
              className="mt-4 inline-flex items-center justify-center w-full h-10 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md text-[15px] font-normal shadow-sm"
            >
              Entendido
            </button>
          </div>
        </div>
      )}

      {/* Help popup */}
      {showHelpPopup && (
        <div className="fixed inset-0 bg-brand-black/30 z-50 flex items-center justify-center p-4">
          <div className="bg-surface-card w-full max-w-md rounded-md shadow-lg p-6 relative max-h-[90vh] overflow-y-auto">
            <button
              type="button"
              onClick={() => setShowHelpPopup(false)}
              className="absolute top-3 right-3 p-2 text-neutral-500 hover:text-neutral-700 rounded-sm focus:outline-none"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-[17px] font-semibold text-brand-black/90 mb-4">
              {hasEdits ? '¿No se modificó la foto que editaste?' : '¿El producto se ve diferente al original?'}
            </h3>
            {hasEdits ? (
              <div className="text-[15px] text-neutral-600 space-y-4">
                <p>Verificá primero que la instrucción que le estás dando a Atlas no sea muy genérica. Por ejemplo, instrucciones como "Arreglá el producto" o "Que se vea mejor" no serán entendidas por la IA. Intentá usar instrucciones puntuales como: "Que el abrigo sea más largo, hasta la altura de las rodillas del modelo" o "Eliminá el bolsillo superior ubicado en la parte delantera del abrigo".</p>
                <p>También probá usar los modos de Edición Precisa y Cambiar Producto en los casos en que creas que los necesitás.</p>
                <p>Si aplicaste estas mejoras y, de igual forma, no se modificó lo que editaste, tené en cuenta que, si intentaste modificar detalles muy específicos del producto o imagen, es posible que Atlas no pueda hacerlo todavía.</p>
                <p>La IA no es perfecta, pero trabajamos día a día para mejorar. En estos casos, es mejor intentar generar una nueva imagen aclarando los detalles en la escena o subiendo una mejor foto del producto.</p>
              </div>
            ) : (
              <div className="text-[15px] text-neutral-600 space-y-4">
                <p>Si en la imagen generada el producto se ve deformado o distinto a como debería ser, antes de editar, verificá que en la foto que has subido se vean bien todos los detalles.</p>
                {userRubro === 'Moda e Indumentaria' && (
                  <p>Atlas entiende mejor tu producto cuando subís una foto del mismo en contexto. Es decir, si es una prenda, subí una foto de una persona o maniquí usando el producto, pero SIN que se vea la cara de la persona.</p>
                )}
                <p>Atlas ve en la imagen lo que vos ves. Esto quiere decir que si alguna parte del producto no es visible en la foto que subiste, Atlas no sabrá cómo es esa parte del producto.</p>
              </div>
            )}
            <button
              type="button"
              onClick={() => setShowHelpPopup(false)}
              className="mt-6 inline-flex items-center justify-center w-full h-10 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md text-[15px] font-normal shadow-sm"
            >
              Entendido
            </button>
          </div>
        </div>
      )}

      <SubscriptionLimitModal
        isOpen={showSubscriptionLimitModal}
        type={subscriptionLimitType}
        onClose={() => setShowSubscriptionLimitModal(false)}
      />
    </div>
  );
};

export default EditarImagen;
